import { addThingModel, putThingModel } from '@/api';
import { CloseOutlined } from '@ant-design/icons';
import { Button, Drawer, Form, Input, InputNumber, message, Radio, Space } from 'antd';
import React, { forwardRef, useCallback, useImperativeHandle, useState } from 'react';
import { ActionFormItem, EventFormItem, PropertyFormItem } from './components';
import { getActionFormvalue, getEventFormvalue, getFillFormValue, getPropertyFormValue, TypeSpecMap } from './helper';
import { BaseThingModelType, DeatilModalFormValue, ProductInfo, ProductThingModelType, ThingModelType } from './types';
// import { debounce } from 'lodash'
import MonacoEditor from 'react-monaco-editor';
import { v4 } from 'uuid';
import './style.less';

const { useForm } = Form;
const { TextArea } = Input;
// const { Text } = Typography

export interface DetailModalProps {
  productInfo?: ProductInfo;
  onSuccess?: () => void;
  customSaveApi?: {
    update?: (data: any) => Promise<any>;
    add?: (data: any) => Promise<any>;
  };
}

export interface DetailModalRef {
  open: (record?: ProductThingModelType, isEdit?: boolean) => void;
}

export const DetailModal = forwardRef<DetailModalRef, DetailModalProps>(({ productInfo, onSuccess, customSaveApi }, ref) => {
  const [form] = useForm<DeatilModalFormValue>();
  const [record, setRecord] = useState<ProductThingModelType>();
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  // const [nameOptions, setNameOptions] = useState<Array<ProductThingModelType>>([])
  const [isEditRow, setIsEditRow] = useState<boolean>(false);

  useImperativeHandle(
    ref,
    () => {
      return {
        open(row, isEdit) {
          setIsEditRow(!!isEdit);
          setIsModalOpen(true);
          form.setFieldValue('thing_model_type', 'property');
          fillForm(row);
          setRecord(row);
        },
      };
    },
    [form],
  );

  const fillForm = useCallback(
    (record?: ProductThingModelType) => {
      if (!record) return;

      form.setFieldsValue({
        thing_model_type: record?.type,
        name: record?.name,
        code: record?.code,
        description: record?.description,
        tag: record?.tag,
      });

      if (record.type === 'property') {
        form.setFieldsValue({
          access_model: record?.access_mode,
          type: record?.type_spec?.type,
          sort: record?.sort || 1,
          storage_mode: record?.storage_mode || 1,
          external: record?.external ? JSON.stringify(record.external, null, 2) : '{}',
        });

        const specType = record?.type_spec?.type;
        const specs = JSON.parse(record?.type_spec?.specs || '{}');

        form.setFieldsValue(getFillFormValue(specs, specType));
      } else if (record.type === 'event') {
        form.setFieldsValue({
          event_type: record?.event_type,
          structObj: record?.output_params?.map((item) => {
            return {
              ...item,
              id: v4(),
              struct_type: item?.type_spec?.type,
            };
          }),
        });
      } else {
        form.setFieldsValue({
          call_type: record?.call_type,
          structInput: record?.input_params?.map((item) => {
            return {
              ...item,
              id: v4(),
              struct_type: item?.type_spec?.type,
            };
          }),
          structOutput: record?.output_params?.map((item) => {
            return {
              ...item,
              id: v4(),
              struct_type: item?.type_spec?.type,
            };
          }),
        });
      }
    },
    [form],
  );

  const handleClose = useCallback(() => {
    setIsModalOpen(false);
    // setNameOptions([])
    form.resetFields();
  }, []);

  return (
    <Drawer
      title={isEditRow ? '编辑' : record ? '查看' : '添加自定义功能点'}
      placement="right"
      open={isModalOpen}
      width={560}
      closable={false}
      maskClosable={false}
      destroyOnHidden={true}
      styles={{ header: { height: 51 } }}
      onClose={handleClose}
      extra={<CloseOutlined onClick={handleClose} />}
      footer={
        <Space>
          {record && !isEditRow ? (
            <Button onClick={handleClose}>关闭</Button>
          ) : (
            <>
              <Button
                type="primary"
                loading={submitting}
                disabled={submitting}
                onClick={async () => {
                  const formValue = await form.validateFields();
                  const thing_model_type: ThingModelType = formValue?.thing_model_type;

                  const result: BaseThingModelType = {
                    thing_model_type,
                    product_id: productInfo?.id,
                    name: formValue?.name,
                    code: formValue?.code,
                    description: formValue?.description,
                    tag: formValue?.tag || '',
                    id: record?.id,
                    sort: formValue?.sort, // 排序
                    storage_mode: formValue?.storage_mode, // 存储模式1标识存储历史，2代表记录最新值
                    external: JSON.parse(formValue?.external || '{}'),
                  };

                  if (thing_model_type === 'property') {
                    result.property = getPropertyFormValue({ formValue, typeKey: 'type' }).property;
                  } else if (thing_model_type === 'event') {
                    result.event = getEventFormvalue({ formValue }).event;
                  } else if (thing_model_type === 'action') {
                    result.action = getActionFormvalue({ formValue }).action;
                  }
                  setSubmitting(true);
                  // 使用自定义接口或默认接口
                  const updateApi = customSaveApi?.update || putThingModel;
                  const addApi = customSaveApi?.add || addThingModel;
                  const ajax = record ? updateApi : addApi;
                  ajax(result)
                    .then((resp) => {
                      if (resp.success) {
                        message.success(`物模型${isEditRow ? '编辑' : '添加'}成功`);
                        handleClose();
                        if (typeof onSuccess === 'function') {
                          onSuccess();
                        }
                      } else {
                        message.error(resp?.errorMsg);
                      }
                    })
                    .catch((e) => {
                      console.error('物模型保存失败:', e);
                      message.error(e?.errorMsg || '保存失败，请重试');
                    })
                    .finally(() => {
                      setSubmitting(false);
                    });
                }}
              >
                确定
              </Button>
              <Button onClick={handleClose}>取消</Button>
            </>
          )}
        </Space>
      }
    >
      <Form form={form} layout="vertical" disabled={!!record && !isEditRow}>
        <Form.Item label="功能类型" name="thing_model_type" rules={[{ required: true, message: '请选择功能类型' }]} initialValue={'property'}>
          <Radio.Group
            onChange={() => {
              // setNameOptions([])
              form.setFieldValue('name', undefined);
            }}
          >
            <Radio.Button value="property">属性类型</Radio.Button>
            <Radio.Button value="event">事件类型</Radio.Button>
            <Radio.Button value="action">服务类型</Radio.Button>
          </Radio.Group>
        </Form.Item>
        <Form.Item noStyle shouldUpdate={(prevValues, nextValues) => prevValues.thing_model_type !== nextValues.thing_model_type}>
          {() => {
            return (
              <Form.Item
                label="功能名称"
                name="name"
                rules={[{ required: true, message: '请输入功能名称' }]}
                // @ts-ignore
                shouldUpdate={(prevValues, nextValues) => prevValues.thing_model_type !== nextValues.thing_model_type}
                // getValueFromEvent={(value) => {
                //   console.log(value)
                //   const lastInput = value[value.length - 1]
                //   try {
                //     return JSON.parse(lastInput).name || String(lastInput)
                //   } catch (error) {
                //     return lastInput
                //   }
                // }}
              >
                <Input placeholder="请输入功能名称"></Input>
                {/* <Select
                  showSearch
                  allowClear
                  mode="tags"
                  placeholder="请输入功能名称"
                  popupClassName="thingmodel-name-select"
                  style={{ width: '100%' }}
                  maxTagCount={10}
                  showArrow={false}
                  filterOption={false}
                  notFoundContent={null}
                  onSearch={debounce(async (value: string) => {
                    if (value) {
                      const { success, result } = await thingModelSystem<Array<ProductThingModelType>>({
                        modelName: value,
                        thingModelType: getFieldValue('thing_model_type'),
                      })
                      if (success) {
                        setNameOptions(result)
                      }
                    } else {
                      setNameOptions([])
                    }
                  }, 500)}
                  options={(nameOptions || []).map(({ id, name, tag, code }) => ({
                    value: JSON.stringify({ id, name }),
                    label: (
                      <>
                        <div className="mb10">
                          <Text type="secondary">{name}</Text>
                          <Tag className="ml10">{tag}</Tag>
                        </div>
                        <div>
                          <Text type="secondary">标识符: {code}</Text>
                        </div>
                      </>
                    ),
                  }))}
                /> */}
              </Form.Item>
            );
          }}
        </Form.Item>
        <Form.Item
          label="标识符"
          extra="首字符必须为字母，整体由字母、数字或下划线组成，长度不超过 32 个字符。"
          name="code"
          rules={[
            { required: true, message: '请输入标识符' },
            { pattern: /^[A-Za-z\-_#@][A-Za-z0-9\-_#@]*$/, message: '只能输入英文字母、数字和符号 - _ # @，且不能以数字开头' },
          ]}
        >
          <Input placeholder="请输入标识符"></Input>
        </Form.Item>
        <Form.Item noStyle shouldUpdate={(prevValues, nextValues) => prevValues.thing_model_type !== nextValues.thing_model_type}>
          {({ getFieldValue }) => {
            const thing_model_type: ThingModelType = getFieldValue('thing_model_type');

            if (thing_model_type === 'property') {
              return (
                <>
                  <PropertyFormItem
                    options={Object.entries(TypeSpecMap)
                      ?.filter(([value]) => value !== 'struct' && value !== 'array')
                      .map(([value, label]) => ({ label, value }))}
                    showView={!!record && !isEditRow}
                  />
                  <Form.Item
                    label="数据传输类型"
                    name="access_model"
                    rules={[{ required: true, message: '请选择数据传输类型' }]}
                    initialValue={'RW'}
                    extra="可下发可上报：指令数据可以下发给设备，设备数据可以上报给云端；只上报：数据只支持从设备上报；只下发：数据只支持从云端下发；"
                  >
                    <Radio.Group>
                      <Radio value="RW">可下发可上报</Radio>
                      <Radio value="R">只上报</Radio>
                      <Radio value="W">只下发</Radio>
                    </Radio.Group>
                  </Form.Item>
                  <Form.Item
                    label="存储模式"
                    name="storage_mode"
                    rules={[{ required: true, message: '请选择存储模式' }]}
                    initialValue={1}
                    extra="存储历史记录：数据会写入时序数据库，完整保留所有历史数据；存储最新值：数据不写入时序数据库，仅保留当前最新一条记录。"
                  >
                    <Radio.Group>
                      <Radio value={1}>存储历史记录</Radio>
                      <Radio value={2}>存储最新值</Radio>
                    </Radio.Group>
                  </Form.Item>
                  <Form.Item label="排序" required>
                    <Form.Item name="sort" noStyle initialValue={1} rules={[{ required: true, message: '请输入排序' }]}>
                      <InputNumber min={1} />
                    </Form.Item>
                  </Form.Item>
                  <Form.Item
                    label="扩展字段"
                    name="external"
                    key={`external-${record?.id || 'new'}`}
                    rules={[
                      { required: true, message: '请输入json' },
                      {
                        validator: async (_rule, value) => {
                          try {
                            value && JSON.parse(value);
                          } catch {
                            throw new Error('请输入正确的 json 格式');
                          }
                        },
                      },
                    ]}
                    initialValue="{}"
                  >
                    <MonacoEditor
                      key={`monaco-${record?.id || 'new'}`}
                      height={300}
                      language="json"
                      theme="vs-dark"
                      options={{
                        readOnly: !!record && !isEditRow,
                      }}
                      editorWillMount={(monaco) => {
                        monaco.languages.json.jsonDefaults.setDiagnosticsOptions({
                          validate: true,
                          allowComments: true,
                          schemas: [],
                        });
                      }}
                      editorDidMount={(editor) => {
                        // 延迟执行layout，确保容器尺寸已经确定
                        setTimeout(() => {
                          editor.layout();
                        }, 100);
                      }}
                    ></MonacoEditor>
                  </Form.Item>
                  <Form.Item label="标签" name="tag">
                    <Input placeholder="请输入标签"></Input>
                  </Form.Item>
                </>
              );
            }

            if (thing_model_type === 'event') {
              return <EventFormItem showView={!!record && !isEditRow} />;
            }

            if (thing_model_type === 'action') {
              return <ActionFormItem showView={!!record && !isEditRow} />;
            }

            return null;
          }}
        </Form.Item>
        <Form.Item label="描述" name="description">
          <TextArea placeholder="请输入描述" rows={4}></TextArea>
        </Form.Item>
      </Form>
    </Drawer>
  );
});

export default DetailModal;
